import { httpClient } from './http'
import type {
  User,
  UserRequest,
  LoginRequest,
  LoginResponse,
  UserUpdateRequest,
  PasswordChangeRequest,
  Post,
  PostRequest,
  Reservation,
  ReservationRequest,
  SeatStatusResponse,
  ReservationVerifyResponse,
  Reply,
  ReplyRequest,
  Notice,
  NoticeRequest,
  Qna,
  QnaRequest,
  PageableResponse,
  ApiResponse
} from '@/types'

// 인증 API
export const authApi = {
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    return await httpClient.post('/auth/login', credentials)
  },

  async register(userData: UserRequest): Promise<ApiResponse<User>> {
    return await httpClient.post('/auth/register', userData)
  },

  async getMe(): Promise<ApiResponse<User>> {
    return await httpClient.get('/auth/me')
  },

  async findId(name: string, phone: string): Promise<ApiResponse<string>> {
    return await httpClient.post('/auth/find-id', { name, phone })
  },

  async resetPassword(email: string): Promise<ApiResponse<void>> {
    return await httpClient.post('/auth/reset-password', { email })
  },

  async updateProfile(userData: UserUpdateRequest): Promise<ApiResponse<User>> {
    return await httpClient.put('/users/me', userData)
  },

  async changePassword(passwordData: PasswordChangeRequest): Promise<ApiResponse<void>> {
    return await httpClient.put('/users/me/password', passwordData)
  }
}

// 게시글 API
export const postApi = {
  async getPosts(params?: {
    keyword?: string
    userId?: number
    page?: number
    size?: number
  }): Promise<PageableResponse<Post>> {
    const response = await httpClient.get('/posts', { params })
    return response?.data
  },

  async getPost(id: number): Promise<ApiResponse<Post>> {
    return await httpClient.get(`/posts/${id}`)
  },

  async getMyPosts(params?: {
    page?: number
    size?: number
  }): Promise<PageableResponse<Post>> {
    const response = await httpClient.get('/posts/my', { params })
    return response?.data
  },

  async createPost(postData: PostRequest): Promise<ApiResponse<Post>> {
    return await httpClient.post('/posts', postData)
  },

  async updatePost(id: number, postData: PostRequest): Promise<ApiResponse<Post>> {
    return await httpClient.put(`/posts/${id}`, postData)
  },

  async deletePost(id: number): Promise<ApiResponse<void>> {
    return await httpClient.delete(`/posts/${id}`)
  }
}

// 예약 API
export const reservationApi = {
  async getMyReservations(params?: {
    activeOnly?: boolean
    page?: number
    size?: number
  }): Promise<PageableResponse<Reservation>> {
    const response = await httpClient.get('/reservations/my', { params })
    return response?.data
  },

  async getPostSeats(postId: number): Promise<ApiResponse<any>> {
    return await httpClient.get(`/reservations/posts/${postId}/seats`)
  },

  async getPostSeatStatus(postId: number): Promise<ApiResponse<SeatStatusResponse>> {
    return await httpClient.get(`/reservations/posts/${postId}/seat-status`)
  },

  async getPostSeatStatuses(postIds: number[]): Promise<ApiResponse<SeatStatusResponse[]>> {
    const params = { postIds: postIds.join(',') }
    return await httpClient.get('/reservations/posts/seat-status', { params })
  },

  async getMySeatNumbers(postId: number): Promise<ApiResponse<number[]>> {
    return await httpClient.get(`/reservations/posts/${postId}/my-seats`)
  },

  async getPostReservations(postId: number): Promise<ApiResponse<Reservation[]>> {
    return await httpClient.get(`/reservations/posts/${postId}`)
  },

  async checkUserReservation(postId: number): Promise<ApiResponse<boolean>> {
    return await httpClient.get(`/reservations/posts/${postId}/check`)
  },

  async createReservation(reservationData: ReservationRequest): Promise<ApiResponse<Reservation[]>> {
    return await httpClient.post('/reservations', reservationData)
  },

  async cancelReservation(id: number): Promise<ApiResponse<void>> {
    return await httpClient.delete(`/reservations/${id}`)
  },

  async verifyReservation(token: string): Promise<ApiResponse<ReservationVerifyResponse>> {
    return await httpClient.get('/reservations/verify', { params: { token } })
  }
}

// 댓글 API
export const replyApi = {
  async getReplies(postId: number, params?: {
    page?: number
    size?: number
  }): Promise<PageableResponse<Reply>> {
    const response = await httpClient.get(`/replies/posts/${postId}`, { params })
    return response?.data
  },

  async createReply(replyData: ReplyRequest): Promise<ApiResponse<Reply>> {
    return await httpClient.post('/replies', replyData)
  },

  async updateReply(id: number, content: string): Promise<ApiResponse<Reply>> {
    return await httpClient.put(`/replies/${id}`, { content })
  },

  async deleteReply(id: number): Promise<ApiResponse<void>> {
    return await httpClient.delete(`/replies/${id}`)
  }
}

// 공지사항 API
export const noticeApi = {
  async getNotices(params?: {
    keyword?: string
    page?: number
    size?: number
  }): Promise<PageableResponse<Notice>> {
    const response = await httpClient.get('/notices', { params })
    return response?.data
  },

  async getNotice(id: number): Promise<ApiResponse<Notice>> {
    return await httpClient.get(`/notices/${id}`)
  },

  async createNotice(noticeData: NoticeRequest): Promise<ApiResponse<Notice>> {
    return await httpClient.post('/notices', noticeData)
  },

  async updateNotice(id: number, noticeData: NoticeRequest): Promise<ApiResponse<Notice>> {
    return await httpClient.put(`/notices/${id}`, noticeData)
  },

  async deleteNotice(id: number): Promise<ApiResponse<void>> {
    return await httpClient.delete(`/notices/${id}`)
  }
}

// Q&A API
export const qnaApi = {
  async getQnas(params?: {
    keyword?: string
    page?: number
    size?: number
  }): Promise<PageableResponse<Qna>> {
    const response = await httpClient.get('/qnas', { params })
    return response?.data
  },

  async getQna(id: number): Promise<ApiResponse<Qna>> {
    return await httpClient.get(`/qnas/${id}`)
  },

  async createQna(qnaData: QnaRequest): Promise<ApiResponse<Qna>> {
    return await httpClient.post('/qnas', qnaData)
  },

  async updateQna(id: number, qnaData: QnaRequest): Promise<ApiResponse<Qna>> {
    return await httpClient.put(`/qnas/${id}`, qnaData)
  },

  async deleteQna(id: number): Promise<ApiResponse<void>> {
    return await httpClient.delete(`/qnas/${id}`)
  }
}

// 관리자 API
export const adminApi = {
  async getUsers(params?: {
    keyword?: string
    page?: number
    size?: number
  }): Promise<PageableResponse<User>> {
    const response = await httpClient.get('/admin/users', { params })
    return response?.data
  },

  async deleteUser(id: number): Promise<ApiResponse<void>> {
    return await httpClient.delete(`/admin/users/${id}`)
  },

  async changeUserRole(id: number, data: { role: string }): Promise<ApiResponse<User>> {
    return await httpClient.put(`/admin/users/${id}/role`, data)
  },

  async getDashboardStats(): Promise<ApiResponse<any>> {
    return await httpClient.get('/admin/dashboard')
  },

  async getSystemInfo(): Promise<ApiResponse<any>> {
    return await httpClient.get('/admin/system-info')
  }
}
